import React, { useCallback, useEffect, useMemo, useState } from "react";
import Container from "@material-ui/core/Container";
import { connect } from "react-redux";
import {
  useDependency,
  useEntityProgress,
  useMasterDetailWithDialog,
  useSortHandler,
} from "reactcoregk/hooks";
import AttacksTable from "./attacks-table";
import Subheader from "./subheader";
import {
  useCurrentPageParam,
  useDefaultTable,
  usePageableEntity,
  useSortHandlerPath,
  useTableCells,
} from "../../helpers/hooks";
import SubmenuLayout from "../../components/Layouts/SubmenuLayout";
import { Schema } from "../../store/@core/schema";
import { EntityType } from "../../store/@core/entityType";
import Grid from "@material-ui/core/Grid";
import Box from "@material-ui/core/Box";
import { Pagination } from "@material-ui/lab";
import {
  createAttack,
  getAllAttacks,
  updateAttack,
} from "../../store/attack/actions";
import EditDialog from "./EditDialog";
import { getAllTechniques } from "../../store/technique/actions";
import { fetchData } from "helpers/utils";
import { API_URL } from "config";
import _ from "lodash";
import { tableSettingKeys } from "constants/enums";
import TableSettingsDialog from "components/Common/TableSettingsDialog";

const defaultCells = [
  {
    id: "id",
    numeric: false,
    label: "ID",
    isSortable: true,
    default: true,
  },
  {
    id: "name",
    numeric: false,
    label: "Name",
    default: true,
  },
  {
    id: "category",
    numeric: false,
    label: "Category",
    default: true,
  },
  {
    id: "type",
    numeric: false,
    label: "Type",
    default: true,
  },
  {
    id: "attackPhase",
    numeric: false,
    label: "Attack Phase",
    default: true,
  },
  {
    id: "target",
    numeric: false,
    label: "Target",
    default: true,
  },
  {
    id: "status",
    numeric: false,
    label: "Status",
    default: true,
  },
  {
    id: "level",
    numeric: false,
    label: "Level",
    default: true,
  },
  {
    id: "createdAt",
    numeric: false,
    label: "Created At",
    isSortable: true,
    default: true,
  },
  {
    id: "updatedAt",
    numeric: false,
    label: "Updated At",
    isSortable: true,
    default: true,
  },
  {
    id: "createdByUsername",
    numeric: false,
    label: "Created By",
    isSortable: false,
    default: true,
  },
  {
    id: "updatedByUsername",
    numeric: false,
    label: "Updated By",
    isSortable: false,
    default: true,
  },
];

function Attacks(props) {
  const {
    context,
    getAllAttacks,
    createAttack,
    updateAttack,
    getAllTechniques,
  } = props;

  const [openSettings, setOpenSettings] = useState(false);

  // Handlers for subheader controls
  const rootParams = props.location.search.replace("?", "&");
  const [currentPage, setCurrentPage] = useCurrentPageParam();
  const [openActors, setOpenActors] = useState(false);
  const [openCves, setOpenCves] = useState(false);

  const [categories, setCategories] = useState([]);
  const [categoryAndTypes, setCategoryAndTypes] = useState([]);

  // Handlers for new or existing entity to show in dialog
  const { openModal, handleCloseModal, handleCreateNew, entity } =
    useMasterDetailWithDialog(Schema[EntityType.Attack]);

  // Sort handler used by default table
  const sortHandler = useSortHandler("updatedAt", "desc");

  // Callback to trigger on useDependency hook
  const initializeDependency = useCallback(() => {
    getAllAttacks("?page=0&size=20");
  }, [getAllAttacks]);

  useDependency(context.Technique, getAllTechniques);

  // Refresh table after a successful post
  useEntityProgress(context.Attack, initializeDependency);

  const { cells, storedCells, optionalCellRender } = useTableCells(
    context,
    tableSettingKeys.ATTACKS,
    defaultCells,
    false
  );

  // Get state for current entity, and handler to manage paging
  const { list, totalPages, isLoading } = usePageableEntity(
    context.Attack,
    getAllAttacks,
    rootParams
  );

  useEffect(() => {
    fetchData(API_URL + "/api/bas/categories")
      .then((res) => {
        const cat = _.filter(res, (x) => x.entityType === "BAS_ATTACK");
        setCategories(cat);
        setCategoryAndTypes(
          _.reduce(
            cat ?? [],
            (acc, item) => {
              acc[0].push(item.name);
              acc[1].push(..._.map(item.types, "name"));
              return acc;
            },
            [[], []]
          )
        );
      })
      .catch((e) => {});
  }, []);

  useSortHandlerPath(sortHandler.sort);

  // Init default table
  const defaultTable = useDefaultTable(list, sortHandler);

  // Create the subheader options
  const AttackMenu = (
    <Subheader
      handleCreateNew={handleCreateNew}
      selected={defaultTable.selected}
      openSettings={() => setOpenSettings(true)}
      categoryAndTypes={categoryAndTypes}
    />
  );

  const formattedList = useMemo(() => {
    if (categories && categories.length > 0) {
      return list.map((listItem) => {
        return {
          ...listItem,
          category: categories
            .filter((item) => listItem.categoryIds?.includes(item.id))
            .map((item) => ({
              name: item.name,
              types: item.types
                .filter((type) => listItem.categoryTypeIds?.includes(type.id))
                .map((type) => type.name),
            })),
        };
      });
    } else {
      return [...list];
    }
  }, [list, categories]);

  return (
    <SubmenuLayout MenuItemsComponent={AttackMenu} title={"Attacks"}>
      <Container maxWidth={false}>
        <AttacksTable
          headCells={cells}
          isLoading={isLoading}
          defaultTable={defaultTable}
          attacks={formattedList}
          handleEntityClick={(entity) => {
            props.history.push("/attacks/" + entity.id);
          }}
          optionalCellRender={optionalCellRender}
        />
        <Box mt={2} component={Grid} container justify={"center"}>
          <Pagination
            onChange={(e, newPage) => setCurrentPage(newPage - 1)}
            page={currentPage}
            count={totalPages}
            showFirstButton
            showLastButton
            size={"small"}
          />
        </Box>
      </Container>
      <EditDialog
        context={context}
        open={openModal}
        handleClose={handleCloseModal}
        entity={entity}
        techs={context.Technique.getAll.result}
        getAllAttacks={getAllAttacks}
        openActors={openActors}
        setOpenActors={setOpenActors}
        openCves={openCves}
        setOpenCves={setOpenCves}
        updateEntity={updateAttack}
        entityType={EntityType.Attack}
      />
      <TableSettingsDialog
        open={openSettings}
        handleClose={() => setOpenSettings(false)}
        storedCells={storedCells}
        defaultCells={defaultCells}
        tableKey={tableSettingKeys.ATTACKS}
      />
    </SubmenuLayout>
  );
}

const mapStateToProps = (state) => {
  return {
    context: state,
  };
};
export default connect(mapStateToProps, {
  getAllAttacks,
  createAttack,
  updateAttack,
  getAllTechniques,
})(Attacks);
